<script setup lang="ts">
import { Money } from '@commercetools/platform-sdk';
import { useAnnouncer } from '@vue-a11y/announcer';
import { useVModel } from '@vueuse/core';
import { useStore } from 'vuex';

import AddToCartButtonGroup from '@/components/base/add-to-cart/AddToCartButtonGroup.vue';
import { useCart } from '@/composables/useCart';
import money from '@/filters/money';

const props = withDefaults(
  defineProps<{
    dataTest?: string;
    disabled?: boolean;
    isLoading?: boolean;
    maximumPiecesPerOrder?: number;
    modelValue: number;
    totalCost: Money;
  }>(),
  {
    disabled: false,
    modelValue: 1,
  },
);

const emit = defineEmits(['update:modelValue', 'submit']);

const { polite } = useAnnouncer();
const store = useStore();

const { maxQuantity } = useCart(store);

const quantity = useVModel(props, 'modelValue', emit);

const handleAddToCart = () => {
  polite(`adding ${quantity.value} item${quantity.value > 1 ? 's' : ''} to your cart`);
  emit('submit');
};
</script>

<template>
  <AddToCartButtonGroup
    v-model:quantity="quantity"
    :data-test="dataTest || 'add-to-cart'"
    :disabled="disabled"
    :isLoading="isLoading"
    :maximumPiecesPerOrder="maximumPiecesPerOrder ?? maxQuantity"
    theme="green"
    type="submit"
    @submit="handleAddToCart"
  >
    <slot>Add to Cart &ndash; {{ money(totalCost) }}</slot>
  </AddToCartButtonGroup>
</template>

<style scoped>
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
/* Firefox */
input[type='number'] {
  -moz-appearance: textfield;
}
</style>
